import { Link, NavLink, Outlet, useLocation } from 'react-router';

import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { ProfileMobileNavMenu } from '@/platform/features/profile/profile-settings-nav';
import { activeProfileNavLink, profileNavLinks } from '@/platform/lib/profile-nav';
import { PROFILE_OVERVIEW_PATH } from '@/platform/lib/profile-paths';
import type { ProfileNavLink } from '@/platform/lib/profile-paths';

/**
 * `/profil/*`: sidebar on desktop; hamburger sheet below `lg`.
 */
export function ProfileLayout() {
  const me = useMe();
  const location = useLocation();
  const links = profileNavLinks(me.data);
  const active = activeProfileNavLink(links, location.pathname);

  return (
    <div className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-8">
      <aside className="hidden lg:block" aria-label="Profil">
        <p className="mb-2 px-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          <Link to={PROFILE_OVERVIEW_PATH} className="hover:text-foreground">
            Profil
          </Link>
        </p>
        <ProfileNavList links={links} orientation="vertical" />
      </aside>

      <div className="min-w-0">
        <div className="mb-4 flex items-center gap-3 lg:hidden">
          <ProfileMobileNavMenu links={links} />
          <p className="min-w-0 truncate text-base font-semibold text-primary">
            {active?.label ?? 'Profil'}
          </p>
        </div>
        <Outlet />
      </div>
    </div>
  );
}

function ProfileNavList({
  links,
  orientation,
}: {
  links: ProfileNavLink[];
  orientation: 'vertical' | 'horizontal';
}) {
  const vertical = orientation === 'vertical';
  return (
    <ul className={cn(vertical ? 'space-y-0.5' : 'flex gap-1 whitespace-nowrap')}>
      {links.map((link) => (
        <li key={link.id}>
          <NavLink
            to={link.to}
            end={link.end}
            className={({ isActive }) =>
              cn(
                'block text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                vertical
                  ? 'rounded-md px-3 py-2 hover:bg-accent'
                  : '-mb-px border-b-2 border-transparent px-3 py-2.5 hover:text-foreground',
                isActive
                  ? vertical
                    ? 'bg-accent font-medium text-foreground'
                    : 'border-primary font-medium text-foreground'
                  : 'text-muted-foreground',
              )
            }
          >
            {link.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
